<template>
    <div class="tw-flex tw-flex-col tw-gap-2 tw-h-full tw-relative tw-bg-slate-50">
        <Banner v-if="!combinedActivities.length">There isn't any activity to show here just yet</Banner>
        <LoadingSpinner v-else-if="loading" />
        <div
            v-else
            ref="commentContainer"
            class="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-overflow-y-auto tw-px-3 tw-pt-3 tw-pb-20 custom-scrollbar"
        >
            <div
                v-for="combinedActivity in combinedActivities"
                :key="combinedActivity.activity_type + '_' + combinedActivity.id"
                class="tw-relative"
                :class="{
                    '!-tw-mt-1': combinedActivity.activity_type === 'comment' && combinedActivity.hideUser,
                }"
            >
                <template v-if="combinedActivity.activity_type === 'comment'">
                    <!-- Comment bubble -->
                    <div
                        class="tw-flex tw-gap-2 tw-items-start tw-group"
                        :class="{
                            'tw-flex-row-reverse': Auth.user.id === combinedActivity.user.id,
                        }"
                    >
                        <!-- Avatar -->
                        <div class="tw-flex-shrink-0 tw-mt-0.5">
                            <User
                                :user="combinedActivity.user"
                                size="xsmall"
                                :class="{
                                    'tw-invisible': combinedActivity.hideUser,
                                }"
                            />
                        </div>

                        <!-- Message container -->
                        <div
                            class="tw-flex tw-flex-col tw-gap-1 tw-max-w-[82%]"
                            :class="{
                                'tw-items-end': Auth.user.id === combinedActivity.user.id,
                                'tw-items-start': Auth.user.id !== combinedActivity.user.id,
                            }"
                        >
                            <!-- User name and timestamp header -->
                            <div
                                v-if="!combinedActivity.hideUser"
                                class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-0.5 tw-px-1"
                            >
                                <span class="tw-text-[11px] tw-font-medium tw-leading-4 tw-text-slate-700">
                                    {{ combinedActivity.user ? combinedActivity.user.name : 'Eventwise' }}
                                </span>
                                <span class="tw-text-[11px] tw-leading-4 tw-text-slate-400">
                                    <span v-if="!combinedActivity.hideDate">
                                        {{ toLocal(combinedActivity.created_at).toLocaleString(DateTime.DATE_MED) }}
                                    </span>
                                    {{ toLocal(combinedActivity.created_at).toLocaleString(DateTime.TIME_24_SIMPLE) }}
                                </span>
                            </div>

                            <!-- Message bubble -->
                            <div
                                class="tw-relative tw-px-3 tw-py-2 tw-rounded-xl tw-shadow-sm tw-transition-all tw-duration-200 group-hover:tw-shadow"
                                :class="{
                                    'tw-bg-white tw-text-gray-800':
                                        !combinedActivity.is_external && Auth.user.id !== combinedActivity.user.id,
                                    'tw-bg-blue-500 tw-text-white':
                                        Auth.user.id === combinedActivity.user.id && !combinedActivity.is_external,
                                    'tw-bg-eventwise tw-text-white': combinedActivity.is_external,
                                    '!tw-py-0 !tw-px-0 !tw-shadow-none': editingComments.includes(combinedActivity.id),
                                    'tw-rounded-tl':
                                        Auth.user.id !== combinedActivity.user.id && !combinedActivity.hideUser,
                                    'tw-rounded-tr':
                                        Auth.user.id === combinedActivity.user.id && !combinedActivity.hideUser,
                                }"
                            >
                                <div
                                    v-if="!editingComments.includes(combinedActivity.id)"
                                    class="tw-text-xs tw-leading-5 tw-break-words"
                                    v-html="combinedActivity.text"
                                ></div>
                                <Textarea
                                    v-else
                                    v-model="combinedActivity.new_text"
                                    rows="3"
                                    class="tw-min-w-[260px] !tw-flex-1 tw-border-none tw-bg-white tw-text-xs tw-rounded-xl"
                                    @keydown.enter="onUpdateComment($event, combinedActivity)"
                                    @keydown.esc="
                                        editingComments = editingComments.filter((id) => id !== combinedActivity.id)
                                    "
                                />

                                <!-- External comment indicator badge -->
                                <div
                                    v-if="
                                        combinedActivity.is_external && !editingComments.includes(combinedActivity.id)
                                    "
                                    class="tw-absolute -tw-top-1.5 -tw-right-1.5 tw-bg-white tw-rounded-full tw-p-0.5 tw-shadow-sm"
                                >
                                    <Tooltip label="External comment">
                                        <i class="mdi mdi-earth tw-text-eventwise tw-text-sm"></i>
                                    </Tooltip>
                                </div>
                            </div>

                            <!-- Metadata and actions -->
                            <div
                                v-if="
                                    getLastEditor(combinedActivity) ||
                                    (combinedActivity.is_external && !combinedActivity.target_comment)
                                "
                                class="tw-flex tw-gap-2 tw-items-center tw-text-[11px] tw-text-gray-500 tw-px-1"
                                :class="{
                                    'tw-flex-row-reverse': Auth.user.id === combinedActivity.user.id,
                                }"
                            >
                                <!-- Edited indicator -->
                                <Button
                                    v-if="getLastEditor(combinedActivity)"
                                    type="is-link"
                                    class="!tw-p-0 tw-font-normal tw-text-xs !tw-text-gray-500 hover:!tw-text-gray-700 tw-transition-colors"
                                    @click="viewCommentActivity(combinedActivity)"
                                >
                                    <i class="mdi mdi-pencil tw-text-xs"></i>
                                    Edited
                                </Button>

                                <!-- Edit actions for external comments -->
                                <template v-if="combinedActivity.is_external && !combinedActivity.target_comment">
                                    <span v-if="getLastEditor(combinedActivity)" class="tw-text-gray-300">•</span>
                                    <Button
                                        v-if="!editingComments.includes(combinedActivity.id)"
                                        type="is-link"
                                        class="!tw-p-0 tw-text-xs !tw-text-eventwise hover:!tw-text-eventwise-dark tw-font-medium tw-transition-colors"
                                        @click="
                                            () => {
                                                combinedActivity.new_text = combinedActivity.text;
                                                editingComments.push(combinedActivity.id);
                                            }
                                        "
                                    >
                                        <i class="mdi mdi-pencil tw-text-xs"></i>
                                        Edit
                                    </Button>
                                    <template v-else>
                                        <Button
                                            type="is-link"
                                            class="!tw-p-0 tw-text-xs !tw-text-gray-500 hover:!tw-text-gray-700 tw-font-medium tw-transition-colors"
                                            @click="
                                                () => {
                                                    editingComments = editingComments.filter(
                                                        (id) => id !== combinedActivity.id
                                                    );
                                                }
                                            "
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="is-link"
                                            class="!tw-p-0 tw-text-xs !tw-text-eventwise hover:!tw-text-eventwise-dark tw-font-medium tw-transition-colors"
                                            @click="onUpdateComment(null, combinedActivity)"
                                        >
                                            Save
                                        </Button>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <!-- Activity timeline item -->
                    <div class="tw-relative tw-grid tw-grid-cols-[28px_minmax(0,1fr)] tw-gap-2 tw-py-1">
                        <div class="tw-relative tw-flex tw-justify-center tw-items-start">
                            <User
                                class="tw-relative tw-z-10 tw-rounded-full"
                                :user="combinedActivity.user ?? systemUser"
                                size="xsmall"
                            />
                        </div>

                        <!-- Activity content -->
                        <div
                            class="tw-min-w-0 tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-left tw-shadow-sm"
                        >
                            <div class="tw-flex tw-flex-col tw-gap-0.5">
                                <div
                                    class="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-1.5 tw-gap-y-0.5 tw-text-xs tw-leading-5"
                                >
                                    <span class="tw-font-medium tw-text-slate-900">
                                        {{ combinedActivity.user ? combinedActivity.user.name : 'Eventwise' }}
                                    </span>
                                    <span
                                        class="tw-min-w-0 tw-break-words tw-text-slate-600"
                                        v-html="combinedActivity.description"
                                    ></span>
                                </div>

                                <!-- Timestamp -->
                                <div class="tw-text-[11px] tw-leading-4 tw-text-slate-400">
                                    {{ toLocal(combinedActivity.created_at).toLocaleString(DateTime.DATETIME_MED) }}
                                </div>
                            </div>

                            <!-- Activity comment detail -->
                            <div
                                v-if="combinedActivity.comment"
                                class="tw-mt-2 tw-border-t tw-border-slate-100 tw-pt-2"
                            >
                                <div
                                    class="tw-rounded tw-bg-slate-50 tw-px-2.5 tw-py-1.5 tw-text-[11px] tw-leading-4 tw-text-slate-700 tw-break-words"
                                >
                                    {{ combinedActivity.comment }}
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Input area -->
        <div
            class="tw-sticky tw-bottom-0 tw-left-0 tw-z-10 tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border-t tw-border-gray-200 tw-p-2"
        >
            <Textarea
                v-model="model"
                class="tw-max-h-[64px] tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-ring-2 focus:tw-ring-eventwise focus:tw-border-transparent tw-transition-all"
                :class="{
                    '!tw-pb-6': allowExternal,
                }"
                maxlength="255"
                :placeholder="`Type a comment and press ${getPlatform() === 'Mac' ? 'Cmd' : 'Ctrl'}+Enter to send`"
                :rows="1"
                expandable
                @keydown.enter="onSaveComment"
            ></Textarea>
            <Button
                :disabled="!model"
                type="is-primary"
                class="tw-h-9 tw-w-9 tw-rounded-md tw-shadow-sm hover:tw-shadow tw-transition-all tw-flex tw-items-center tw-justify-center"
                @click="onSaveComment"
            >
                <i class="mdi mdi-send"></i>
            </Button>
            <Checkbox
                v-if="allowExternal"
                v-model="displayExternally"
                class="!tw-absolute tw-bottom-4 tw-left-6 tw-text-xs"
            >
                Display this comment externally
            </Checkbox>
        </div>
    </div>
    <Modal ref="activityModal" title="Comment History" size="is-xxxlarge" paddingless>
        <DataTable
            v-if="viewingComment"
            :data="viewingComment.activity"
            :columns="[
                {
                    title: 'Date',
                    field: 'created_at',
                },
                {
                    title: 'User',
                    field: 'user',
                },
                {
                    title: 'Action',
                    field: 'description',
                },
                {
                    title: 'Details',
                    field: 'comment',
                },
            ]"
        >
            <template #column(created_at)="{ row }">
                {{ toLocal(row.created_at).toLocaleString(DateTime.DATETIME_FULL) }}
            </template>
            <template #column(user)="{ row }">
                <User :user="row.user" size="xsmall" :hide-name="false" />
            </template>
            <template #columns(description)="row">
                {{ row.description }}
            </template>
            <template #column(comment)="{ row }">
                <span
                    v-html="
                        row.source === 'user_diff'
                            ? htmlDiff(JSON.parse(row.comment).old, JSON.parse(row.comment).new)
                            : row.comment
                    "
                ></span>
            </template>
        </DataTable>
    </Modal>
</template>
<script setup>
import Textarea from '@/js/components/controls/Textarea.vue';
import Button from '@/js/components/controls/Button.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import User from '@/js/components/widgets/User.vue';
import Modal from '@/js/components/Modal.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import { toLocal, getPlatform, htmlDiff } from '@/js/utils.js';
import { DateTime } from 'luxon';
import { computed, inject, onMounted, ref, useTemplateRef, watch } from 'vue';
import Tooltip from '@/js/components/Tooltip.vue';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';

const props = defineProps({
    activity: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    comments: {
        type: Array,
        default: () => [],
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    allowExternal: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['comment', 'external-comment', 'update-comment']);
const model = defineModel({
    type: String,
    default: '',
});
const displayExternally = ref(false);
const editingComments = ref([]);
const commentContainer = useTemplateRef('commentContainer');
const viewingComment = ref(null);
const activityModal = useTemplateRef('activityModal');
const systemUser = {
    id: 0,
    name: 'Eventwise',
};

const onSaveComment = (e) => {
    if (e.type !== 'click' && !e.metaKey && !e.ctrlKey) {
        return;
    }
    e.preventDefault();
    emit(props.allowExternal && displayExternally.value ? 'external-comment' : 'comment', model.value);
    model.value = '';
};
const onUpdateComment = (e, comment) => {
    if (e && !e.metaKey && !e.ctrlKey) {
        return;
    }
    e ? e.preventDefault() : null;
    editingComments.value = editingComments.value.filter((id) => id !== comment.id);
    if (comment.text == comment.new_text) {
        return;
    }
    comment.text = comment.new_text;
    delete comment.new_text;
    emit('update-comment', comment);
};
const viewCommentActivity = (comment) => {
    viewingComment.value = comment;
    activityModal.value.open();
};

const getLastEditor = (comment) => {
    if (!comment.activity) {
        return false;
    }
    for (let index = comment.activity.length - 1; index >= 0; index--) {
        if (comment.activity[index].source === 'user_diff') {
            return comment.activity[index].user;
        }
    }
    return false;
};

onMounted(() => {
    resetScroll();
});

watch(
    () => props.comments,
    () => {
        resetScroll();
    }
);
watch(
    () => props.activity,
    () => {
        resetScroll();
    }
);

/**
 * If this component is used in a tab, we need to inject the activeTab context
 * as if the tab this is within isn't displayed by default, the onMounted hook
 * will still get called but not adequately resize the textarea.
 */
const activeTabContext = inject('activeTab', null);
watch(
    () => activeTabContext,
    () => {
        setTimeout(() => {
            resetScroll();
        }, 10);
    }
);

const resetScroll = () => {
    if (!commentContainer.value) {
        return;
    }
    commentContainer.value.scrollTop = commentContainer.value.scrollHeight;
};

const connectedComments = computed(() => {
    const newComments = JSON.parse(JSON.stringify(props.comments));
    let lastComment = null;
    return newComments.map((comment) => {
        const hideUser = lastComment && lastComment.user.id === comment.user.id;
        const lastCreatedAt = lastComment ? toLocal(lastComment.created_at) : null;
        const hideDate =
            lastCreatedAt &&
            lastCreatedAt.toLocaleString(DateTime.DATE_FULL) ===
                toLocal(comment.created_at).toLocaleString(DateTime.DATE_FULL);
        lastComment = comment;
        return {
            activity_type: 'comment',
            ...comment,
            hideUser,
            hideDate,
        };
    });
});

const combinedActivities = computed(() => {
    return [...props.activity, ...connectedComments.value].sort((x, y) => {
        return DateTime.fromISO(x.created_at) - DateTime.fromISO(y.created_at);
    });
});
</script>

<style lang="scss" scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.3);
    border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.5);
}

/* Firefox */
.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

:deep(.mentionable) {
    @apply hover:tw-text-eventwise tw-underline tw-decoration-dashed tw-underline-offset-2 tw-decoration-1 tw-decoration-gray-400 hover:tw-decoration-eventwise;

    &.missing {
        @apply tw-select-none tw-bg-gray-200 tw-px-1 tw-py-0.5 tw-rounded-md tw-text-gray-500 tw-relative hover:!tw-text-gray-500;

        &:before {
            font-family: 'Material Design Icons';
            content: '\F02D6';
            @apply tw-mr-0.5;
        }

        &:after {
            content: 'This item is inaccessible due to being missing, deleted or insufficient permissions';
            @apply tw-absolute tw-left-1/2 tw--translate-x-1/2 tw-bottom-full tw-mb-1 tw-z-20 tw-block tw-bg-gray-800 tw-text-white tw-text-[11px] tw-rounded-md tw-px-2 tw-py-1 tw-w-64 tw-text-center tw-whitespace-normal tw-opacity-0 tw-invisible tw-transition-opacity tw-duration-200;
        }

        &:hover:after {
            @apply tw-opacity-100 tw-visible;
        }
    }
}
</style>
